import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { GetAppointmentsWithFeedbackRequiredDTO } from '@bb/domain/dist/types';

import errorReporter from '@app/errorReporter';
import { DuplicateListingController } from 'api';
import { Badge } from 'components';
import { useTasks } from '../hooks';
import { LayoutCard, Loading } from '../components';
import {
  AllTaskSection,
  DuplicatesSection,
  FeedbackSection,
} from '../Section/TaskSection';

type FeedbackRequestedTask =
  GetAppointmentsWithFeedbackRequiredDTO['appointments'];

const TasksCard = () => {
  const [tabKey, setTabKey] = useState('all');
  const {
    isTasksLoading,
    taskItems,
    totalCount,
    taskSearchValue,
    setTaskSearchValue,
    permissionsMap,
    refetchTasks,
  } = useTasks('user');

  const [feedbackItems, setFeedbackItems] = useState<FeedbackRequestedTask>([]);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);

  const [duplicateItems, setDuplicateItems] = useState([]);
  const [isDuplicatesLoading, setIsDuplicatesLoading] = useState(false);

  useEffect(() => {
    setIsFeedbackLoading(true);
    (async () => {
      try {
        const {
          data: { appointments },
        } = await axios.get<GetAppointmentsWithFeedbackRequiredDTO>(
          '/user/coop/appointments/feedback',
        );

        setFeedbackItems(appointments);
      } catch (err) {
        errorReporter.error(err);
      } finally {
        setIsFeedbackLoading(false);
      }
    })();
  }, []);

  const fetchDuplicates = async () => {
    const data =
      await DuplicateListingController.getDuplicateListingsForAgent();
    setDuplicateItems(data);
  };

  useEffect(() => {
    setIsDuplicatesLoading(true);
    (async () => {
      try {
        await fetchDuplicates();
      } catch (err) {
        errorReporter.error(err);
      } finally {
        setIsDuplicatesLoading(false);
      }
    })();
  }, []);

  const getContent = () => {
    switch (tabKey) {
      case 'feedback':
        return <FeedbackSection items={feedbackItems} />;
      case 'duplicates':
        return (
          <DuplicatesSection
            items={duplicateItems}
            fetchDuplicates={fetchDuplicates}
          />
        );
      default:
        return (
          <AllTaskSection
            items={taskItems}
            permissionsMap={permissionsMap}
            refetchTasks={refetchTasks}
          />
        );
    }
  };

  const shouldRenderLoading =
    (tabKey === 'all' && isTasksLoading) ||
    (tabKey === 'feedback' && isFeedbackLoading) ||
    (tabKey === 'duplicates' && isDuplicatesLoading);

  let content: React.ReactElement;
  if (shouldRenderLoading) {
    content = <Loading />;
  } else {
    content = getContent();
  }

  return (
    <LayoutCard
      search={
        tabKey === 'all'
          ? {
              searchValue: taskSearchValue,
              onSearchValueChange: setTaskSearchValue,
            }
          : null
      }
      titleLink={{
        title: (
          <TasksHeaderContainer>
            <span data-cy="view-appts-action-required">{'Tasks'}</span>
            <Badge
              data-cy="tasks-count"
              data-cy-metadata={`${totalCount}`}
              count={totalCount}
            />
          </TasksHeaderContainer>
        ),
        linkPath: `/my_business/action_required`,
      }}
      activeTabKey={tabKey}
      tabList={[
        { key: 'all', tab: <span data-cy="tasks-tab-all">All</span> },
        {
          key: 'feedback',
          tab: <span data-cy="tasks-tab-feedback">Feedback</span>,
        },
        {
          key: 'duplicates',
          tab: <span data-cy="tasks-tab-duplicates">Duplicates</span>,
        },
      ]}
      tabProps={{
        size: 'small',
      }}
      onTabChange={setTabKey}
      data-cy="dashboard-tasks-card"
    >
      {content}
    </LayoutCard>
  );
};

export default TasksCard;

const TasksHeaderContainer = styled.div`
  display: inline-grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  column-gap: 8px;
`;
